import { Controller } from "@hotwired/stimulus"
import ApexCharts from "apexcharts"

export default class extends Controller {
  static targets = ["chart"]
  static values = {
    url: String,
    type: { type: String, default: "donut" },
    height: { type: String, default: "100%" },
    width: { type: String, default: "100%" },
    size: { type: String, default: "65%" },
    legendPosition: { type: String, default: "left" },
    title: { type: String, default: "Total" }
  }

  connect() {
    this.loadChartData()
  }

  disconnect() {
    if (this.chart) {
      this.chart.destroy()
    }
  }

  async loadChartData() {
    if (!this.hasUrlValue) return

    this.element.classList.add("loading")
    try {
      const response = await fetch(this.urlValue)
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

      const data = await response.json()
      this.renderChart(data)
    } catch (error) {
      console.error("Error loading chart data:", error)
    } finally {
      this.element.classList.remove("loading")
    }
  }

  switchType(type) {
    this.typeValue = type
    if (this.chart) {
      this.chart.destroy()
      this.loadChartData()
    }
  }

  updateUrl(event) {
    event.preventDefault()
    const url = event.currentTarget.dataset.url
    if (url) {
      this.urlValue = url

      // Update active state for period buttons
      const buttons = document.querySelectorAll('[data-action="click->pie-chart#updateUrl"]')
      buttons.forEach(button => {
        button.classList.remove('bg-blue-100', 'text-blue-700')
        button.classList.add('bg-gray-100', 'text-gray-700')
      })

      event.currentTarget.classList.remove('bg-gray-100', 'text-gray-700')
      event.currentTarget.classList.add('bg-blue-100', 'text-blue-700')

      // Reload chart data
      if (this.chart) {
        this.chart.destroy()
      }
      this.loadChartData()
    }
  }

  renderChart(data) {
    // Base options for all chart types
    const options = {
      series: data.series,
      chart: {
        type: this.typeValue,
        height: this.heightValue,
        width: this.widthValue,
        toolbar: {
          show: false
        },
        zoom: {
          enabled: false
        },
        offsetX: 0,  // Ensure no horizontal offset
        offsetY: 0,  // Ensure no vertical offset
        sparkline: {
          enabled: false
        }
      },
      colors: data.colors || ['#3b82f6', '#22d3ee', '#e5e7eb'],
      tooltip: {
        enabled: true,
        fillSeriesColor: false,
        theme: 'light',
        style: {
          fontSize: '12px',
          fontFamily: 'system-ui'
        }
      },
      states: {
        hover: {
          filter: {
            type: 'none'
          }
        }
      },
      stroke: {
        width: 3,
        colors: ['rgb(255, 255, 255)']
      },
      responsive: [{
        breakpoint: 1024,
        options: {
          chart: {
            width: "100%"
          },
          legend: {
            show: false,
            position: 'bottom'
          }
        }
      }]
    }

    // Configuration for pie and donut charts
    options.labels = data.labels
    options.dataLabels = {
      enabled: false
    }
    options.grid = {
      padding: {
        top: 0,
        bottom: 0,
        left: 0,
        right: 0
      },
      xaxis: {
        lines: {
          show: false
        }
      },
      yaxis: {
        lines: {
          show: false
        }
      }
    }

    // Remove any parent padding/margin
    options.chart.parentHeightOffset = 0
    options.chart.sparkline = {
      enabled: false
    }

    options.plotOptions = {
      pie: {
        offsetX: 0,  // Ensure pie chart is not centered
        offsetY: 0,  // Ensure pie chart is not centered
        donut: {
          size: this.sizeValue,
          labels: {
            show: true,
            name: {
              show: true,
              fontSize: '13px',
              fontFamily: 'system-ui',
              fontWeight: 400,
              color: '#9ca3af'
            },
            value: {
              show: true,
              fontSize: '15px',
              fontFamily: 'system-ui',
              fontWeight: 600,
              color: '#111827',
              formatter: function (val) {
                return val
              }
            },
            total: {
              show: this.typeValue === 'donut',
              label: this.titleValue || 'Total',
              fontSize: '20px',
              fontFamily: 'system-ui',
              fontWeight: 800,
              color: '#111827',
              formatter: function (w) {
                return w.globals.seriesTotals.reduce((a, b) => a + b, 0)
              }
            }
          }
        }
      }
    }

    options.fill = {
      type: 'gradient',
    }
    // Custom tooltip for donut/pie charts
    options.tooltip = {
      ...options.tooltip,
      custom: function({ series, seriesIndex, dataPointIndex, w }) {
        const value = series[seriesIndex];
        const label = w.globals.labels[seriesIndex];
        const color = w.globals.colors[seriesIndex];
        const total = series.reduce((a, b) => a + b, 0);
        const percentage = ((value / total) * 100).toFixed(1);

        return `
        <div class="shadow-2xl" style="background: ${color}; color: white; padding: 6px 12px; border-radius: 5px;">
          <div class="font-bold text-sm">${label}</div>
          <div style="display: flex; justify-content: space-between; align-items: center;">
            <span class="pr-2 !font-mono">${value}</span>
            <span class="!font-mono font-bold">${percentage}%</span>
          </div>
        </div>
      `;
      }
    }

    options.legend = {
      position: this.legendPositionValue,
      horizontalAlign: 'center',
      fontSize: '12px',
      fontFamily: 'system-ui',
      fontWeight: 400,
      markers: {
        width: 8,
        height: 8,
        radius: 12
      }
    }

    this.chart = new ApexCharts(this.chartTarget, options)
    this.chart.render()
  }
}
